import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const getAuthToken = () =>
  localStorage.getItem('token') || localStorage.getItem('authToken');

const detectContext = () => {
  let portalContext = null;
  try {
    const stored = localStorage.getItem('aiTutorContext');
    if (stored) portalContext = JSON.parse(stored);
  } catch {
    /* ignore */
  }

  const pathname = window.location.pathname;
  let pageType = 'general';
  if (pathname.includes('/studentportal')) pageType = 'student-portal';
  else if (pathname.includes('/academy')) pageType = 'academy';
  else if (pathname.includes('/course')) pageType = 'course';
  else if (pathname.includes('/learning-path') || pathname.includes('/path-dashboard')) pageType = 'learning-path';
  else if (pathname.includes('/virtuallabs')) pageType = 'virtual-labs';
  else if (pathname.includes('/career')) pageType = 'career';
  else if (pathname.includes('/qoretalks')) pageType = 'qore-talks';
  else if (pathname.includes('/qoreconnect') || pathname.includes('/community')) pageType = 'community';
  else if (pathname.includes('/mentorship')) pageType = 'mentorship';
  else if (pathname.includes('/skills')) pageType = 'skills';
  else if (pathname.includes('/aitutoring')) pageType = 'ai-tutoring';
  else if (pathname === '/') pageType = 'home';

  const courseMatch = pathname.match(/\/course\/([^/]+)/);
  const lessonMatch = pathname.match(/\/lesson\/([^/]+)/);

  return {
    page: pathname,
    pageType,
    courseId: courseMatch?.[1] || null,
    lessonId: lessonMatch?.[1] || null,
    timestamp: new Date().toISOString(),
    ...(portalContext || {}),
    activeTab: portalContext?.activeTab || null,
  };
};

export const useQoreAIChat = ({ initialMessages = [] } = {}) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState(initialMessages);
  const [isAITyping, setIsAITyping] = useState(false);
  const [context, setContext] = useState(detectContext);
  const contextRef = useRef(context);

  const refreshContext = useCallback(() => {
    const c = detectContext();
    contextRef.current = c;
    setContext(c);
    return c;
  }, []);

  useEffect(() => {
    refreshContext();
    window.addEventListener('popstate', refreshContext);
    const interval = setInterval(refreshContext, 2000);
    return () => {
      window.removeEventListener('popstate', refreshContext);
      clearInterval(interval);
    };
  }, [refreshContext]);

  const sendMessage = useCallback(async (content) => {
    const trimmed = (content || '').trim();
    if (!trimmed) return;

    const userMsg = {
      id: `${Date.now()}-user`,
      role: 'user',
      content: trimmed,
      timestamp: new Date().toISOString(),
    };

    let historyForApi = [];
    setMessages((prev) => {
      historyForApi = [...prev, userMsg];
      return historyForApi;
    });
    setIsAITyping(true);

    try {
      const token = getAuthToken();
      if (!token) throw new Error('401');

      const apiUrl = `${process.env.REACT_APP_API_URL}/ai/chat`;
      const allMsgs = historyForApi.map((m) => ({
        role: m.role === 'system' ? 'user' : m.role,
        content: m.content,
      }));

      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          messages: allMsgs,
          context: contextRef.current,
          taskType: 'simple_chat',
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      if (data.success) {
        setMessages((prev) => [
          ...prev,
          {
            id: `${Date.now()}-ai`,
            role: 'assistant',
            content: data.data.content,
            timestamp: new Date().toISOString(),
            metadata: data.data.metadata,
          },
        ]);
      } else {
        throw new Error(data.message || 'Failed');
      }
    } catch (err) {
      let text = t('aiTutoring.errorGeneric');
      if (err.message?.includes('Failed to fetch')) text = t('aiTutoring.errorNetwork');
      else if (err.message?.includes('401') || err.message?.includes('403')) text = t('aiTutoring.errorAuth');
      else if (err.message?.includes('500')) text = t('aiTutoring.errorServer');

      setMessages((prev) => [
        ...prev,
        {
          id: `${Date.now()}-err`,
          role: 'system',
          content: text,
          timestamp: new Date().toISOString(),
          isError: true,
        },
      ]);
    } finally {
      setIsAITyping(false);
    }
  }, [t]);

  const newConversation = useCallback(() => {
    setMessages([]);
  }, []);

  const retryLast = useCallback(() => {
    setMessages((prev) => {
      const lastUser = [...prev].reverse().find((m) => m.role === 'user');
      if (!lastUser) return prev;
      const errIdx = prev.map((m) => m.isError).lastIndexOf(true);
      const cleaned = errIdx >= 0 ? prev.filter((_, i) => i !== errIdx) : prev;
      setTimeout(() => sendMessage(lastUser.content), 0);
      return cleaned;
    });
  }, [sendMessage]);

  return {
    messages,
    setMessages,
    isAITyping,
    sendMessage,
    newConversation,
    retryLast,
    context,
    refreshContext,
  };
};

export default useQoreAIChat;